<script setup>
import moment from "moment";
import { onMounted, ref } from "vue";
import ArgonBadge from "@/components/ArgonBadge.vue";
import ArgonSnackbar from "@/components/ArgonSnackbar.vue";
import ArgonTable from "@/components/table/ArgonTable.vue";
import ArgonTd from "@/components/table/ArgonTd.vue";
import ActionsDropdown from "@/components/dropdown/ActionsDropdown.vue";
import ArgonButton from "@/components/ArgonButton.vue";
import CopyValue from "@/components/CopyValue.vue";
import { argonTableStore } from "@/_store/argonTableStore";
import { decaStore } from "@/_store/decaStore";
import { userStore } from "@/_store/userStore";
import { errorUtils, fileUtils } from "@/utils";
import { defaultFilterDates } from "@/maestros/defaultDatesValues";

const store = decaStore();
const tableStore = argonTableStore();
const userStores = userStore();

const snackbar = ref(null);
const customFilterDate = ref(store.customFilterDate);

const fieldFilters = ref([
  { field: "stop_number", description: "Nº parada", exact: true },
  { field: "shipper.name", description: "Cargador" },
  { field: "carrier.name", description: "Transportista" },
  { field: "vehicle.registration_number", description: "Matrícula" },
]);

const defaultFilters = ref([
  {
    field: "stop_date",
    label: "Fecha",
    multi: false,
    isDate: true,
    options: [
      ...defaultFilterDates,
      {
        label: "Personalizado",
        value: customFilterDate,
        isCustomDate: true,
        storage: store,
        formatDate: "DD/MM/YYYY",
      },
    ],
  },
  {
    field: "is_current",
    label: "Versión",
    multi: false,
    options: [
      { label: "Final", value: true },
      { label: "Modificada", value: false },
    ],
  },
]);

const columns = ref([
  { header: "Fecha", field: "stop_date", sort: false, position: "start" },
  { header: "Nº parada", field: "stop_number", sort: false, position: "start" },
  { header: "Cargador", field: "shipper.name", sort: false, position: "start" },
  { header: "Transportista", field: "carrier.name", sort: false, position: "start" },
  { header: "Matrícula", field: "vehicle.registration_number", sort: false, position: "start" },
  { header: "Versión", field: "version", sort: false, position: "center" },
  { header: "Generado", field: "created_at", sort: false, position: "start", width: "w-1"},
  {
    header: "",
    field: "actions",
    sort: false,
    position: "center",
    width: "w-1",
  },
]);

onMounted(() => {
  tableStore.clear();
  const savedFilters = userStores.filtersPage.decas?.filters || [];
  tableStore.changeFilters(savedFilters);
  reloadPage();
});

function reloadPage() {
  userStores.filtersPage.decas = {
    filters: tableStore.filters,
    filtersFavorite: tableStore.filtersFavorite,
  };
  store.getDecas(tableStore.pagination, tableStore.query, tableStore.sort);
}

function onSortChanged() {
  tableStore.clearPagination();
  reloadPage();
}

function getPosition(field) {
  const column = columns.value.find((col) => col.field === field);
  return column?.position || "start";
}

function formatDate(value) {
  if (!value) return "-";
  return moment(value).format("DD/MM/YYYY");
}

function formatDateTime(value) {
  if (!value) return "-";
  return moment(value).format("DD/MM/YYYY HH:mm");
}

function showSelectionError(message) {
  snackbar.value = {
    title: "Error",
    message,
    type: "danger",
    color: "white",
  };
}

function printDeca(stopIds) {
  store
    .printDeca(stopIds)
    .then((bytes) => {
      fileUtils.openBytesFile(bytes, "application/pdf");
    })
    .catch((error) => {
      snackbar.value = {
        title: "Error",
        message: errorUtils.getErrorMessageHtml(
          error,
          "No se pudo imprimir el DeCA."
        ),
        type: "danger",
        color: "white",
      };
    });
}

function printDecaForRow(item) {
  if (!item?.stop_id) return;
  printDeca([item.stop_id]);
}

function printDecaBulk() {
  const ids = Array.isArray(tableStore.checkedList)
    ? tableStore.checkedList.filter(Boolean)
    : [];
  if (ids.length === 0) {
    showSelectionError("Debe seleccionar al menos un DeCA.");
    return;
  }

  const rows = Array.isArray(store.decas?.data) ? store.decas.data : [];
  const stopIds = rows
    .filter((row) => ids.includes(row._id))
    .map((row) => row.stop_id)
    .filter(Boolean);
  if (stopIds.length === 0) return;
  printDeca(stopIds);
}

function getRowActionsMenuId(item) {
  return `deca-row-actions-${String(item?._id || "unknown").replace(
    /[^a-zA-Z0-9_-]/g,
    "_"
  )}`;
}

function getRowActions() {
  return [[{ label: "Imprimir", event: "print" }]];
}

function onRowAction(item, action) {
  if (action?.event === "print") {
    printDecaForRow(item);
  }
}

function closeSnackbar() {
  snackbar.value = null;
}
</script>

<template>
  <div class="card decas-table-view">
    <div class="card-body decas-table-body">
      <argon-table
        v-if="store.decas"
        :columns="columns"
        :items="store.decas"
        :field-filters="fieldFilters"
        :default-filters="defaultFilters"
        :show-new-button="false"
        :show-config-option="true"
        :show-importar="false"
        @filter-changed="reloadPage"
        @on-reload="reloadPage"
        @sort-changed="onSortChanged"
        @page-changed="reloadPage"
      >
        <template #buttons>
          <div
            class="dropdown d-inline"
            data-dropdown-static
            style="--dropdown-gap: 4px"
          >
            <argon-button
              id="navbarDropdownDecaActions"
              color="primary"
              variant="outline"
              class="dropdown-toggle me-2 btn-xs"
              data-bs-toggle="dropdown"
              data-bs-display="static"
              aria-expanded="false"
            >
              <i class="fa fa-exchange me-sm-2"></i>Acciones
            </argon-button>
            <ul
              class="dropdown-menu z-index-1030 dropdown-menu-lg-start px-2 py-3"
              aria-labelledby="navbarDropdownDecaActions"
            >
              <li>
                <a
                  class="dropdown-item border-radius-md"
                  @click.prevent="printDecaBulk"
                >
                  Imprimir
                </a>
              </li>
            </ul>
          </div>
        </template>
        <template #default="{ item }">
          <argon-td
            :position="getPosition('stop_date')"
            :text-primary="formatDate(item.stop_date)"
          />
          <argon-td
            :position="getPosition('stop_number')"
            :text-primary="item.stop_number"
          >
            <template v-if="item.stop_number" #append>
              <copy-value :value-to-copy="String(item.stop_number)" size="xs" show-on-hover />
            </template>
          </argon-td>
          <argon-td
            :position="getPosition('shipper.name')"
            :text-primary="item.shipper?.name"
          />
          <argon-td
            :position="getPosition('carrier.name')"
            :text-primary="item.carrier?.name"
          />
          <argon-td
            :position="getPosition('vehicle.registration_number')"
            :text-primary="item.vehicle?.registration_number"
          >
            <template v-if="item.vehicle?.registration_number" #append>
              <copy-value
                :value-to-copy="String(item.vehicle.registration_number)"
                size="xs"
                show-on-hover
              />
            </template>
          </argon-td>
          <argon-td :position="getPosition('version')">
            <argon-badge color="secondary">V{{ item.version || 1 }}</argon-badge>
          </argon-td>
          <argon-td
            :position="getPosition('created_at')"
            :text-primary="formatDateTime(item.created_at)"
          />
          <argon-td :position="getPosition('actions')">
            <div class="table-row-actions">
              <actions-dropdown
                :menu-id="getRowActionsMenuId(item)"
                :actions="getRowActions()"
                aria-label="Acciones del DeCA"
                trigger-class="table-action-button"
                menu-class="table-action-menu-dropdown"
                @action="(action) => onRowAction(item, action)"
              />
            </div>
          </argon-td>
        </template>
      </argon-table>
    </div>

    <div class="position-fixed top-0 end-0 z-index-2 p-3">
      <argon-snackbar
        v-if="snackbar"
        :title="snackbar.title"
        :description="snackbar.message"
        :icon="{ component: 'ni ni-notification-70', color: snackbar.type }"
        :color="snackbar.color"
        :close-handler="closeSnackbar"
      />
    </div>
  </div>
</template>

<style scoped>
:deep(thead th.text-center.w-1:last-child),
:deep(tbody td.text-center:last-child) {
  max-width: 48px;
  min-width: 48px;
  width: 48px;
}
</style>
